import { useRef, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { arrayMove } from '@dnd-kit/sortable';
import { getPreviousMonthValue } from './useBudgetData';

export const useCategoryMutations = ({
  user,
  currentMonth,
  categories,
  setCategories,
  categoryGroups,
  setCategoryGroups,
  allocations,
  setAllocations,
  setMonthlyIncome,
  setExpandedGroups,
  setToast,
  refreshData,
}) => {
  const categoriesRef = useRef(categories);
  categoriesRef.current = categories;
  const isAddingGroup = useRef(false);

  const addCategory = useCallback(async (form, onSuccess) => {
    const groupId = form.group_id || null;
    const nextSortOrder = categoriesRef.current.filter(c => c.group_id === groupId).length;
    const { data, error } = await supabase.from('categories').insert([{
      user_id: user.id,
      group_id: groupId,
      name: form.name,
      budgeted_amount: parseFloat(form.budgeted_amount) || 0,
      sort_order: nextSortOrder,
    }]).select();
    if (error) { setToast({ message: 'Add failed', type: 'error' }); return; }
    if (data?.[0]) {
      const newCat = data[0];
      const budgetAmount = parseFloat(form.budgeted_amount) || 0;
      await supabase.from('allocations').insert({
        user_id: user.id,
        category_id: newCat.id,
        month: currentMonth,
        amount: budgetAmount,
      });
      setCategories(prev => [...prev, newCat]);
      setAllocations(prev => { const next = new Map(prev); next.set(newCat.id, budgetAmount); return next; });
    }
    setToast({ message: 'Category added!', type: 'success' });
    onSuccess?.();
  }, [user, currentMonth, setCategories, setAllocations, setToast]);

  const addGroup = useCallback(async (form, onSuccess) => {
    if (isAddingGroup.current) return;
    isAddingGroup.current = true;
    try {
      const { data: existing } = await supabase.from('category_groups')
        .select('sort_order').eq('user_id', user.id).order('sort_order', { ascending: false }).limit(1);
      const nextSortOrder = (existing?.[0]?.sort_order ?? 0) + 1;
      const { data, error } = await supabase.from('category_groups').insert([{
        user_id: user.id,
        name: form.name,
        sort_order: nextSortOrder,
      }]).select();
      if (error) { setToast({ message: 'Add failed', type: 'error' }); return; }
      if (data?.[0]) {
        setCategoryGroups(prev => [...prev, data[0]]);
        setExpandedGroups(prev => new Set([...prev, data[0].id]));
      }
      setToast({ message: 'Group added!', type: 'success' });
      onSuccess?.();
    } catch (err) {
      setToast({ message: 'Add failed', type: 'error' });
    } finally {
      isAddingGroup.current = false;
    }
  }, [user, setCategoryGroups, setExpandedGroups, setToast]);

  const updateBudgetInline = useCallback(async (categoryId, newAmount) => {
    const amount = parseFloat(newAmount) || 0;
    if (amount < 0) { setToast({ message: 'Must be positive', type: 'error' }); return; }
    // Optimistic update first
    setAllocations(prev => { const next = new Map(prev); next.set(categoryId, amount); return next; });
    const { error } = await supabase.from('allocations').upsert({
      user_id: user.id,
      category_id: categoryId,
      month: currentMonth,
      amount,
    }, { onConflict: 'user_id,category_id,month' });
    if (error) {
      setToast({ message: 'Save failed', type: 'error' });
    }
  }, [user, currentMonth, setAllocations, setToast]);

  const deleteGroup = useCallback(async (id, onSuccess) => {
    if (!window.confirm('Delete this group and all its categories?')) return;
    const { error } = await supabase.from('category_groups').delete().eq('id', id);
    if (error) { setToast({ message: 'Delete failed', type: 'error' }); return; }
    const removedCatIds = categoriesRef.current.filter(c => c.group_id === id).map(c => c.id);
    setCategoryGroups(prev => prev.filter(g => g.id !== id));
    setCategories(prev => prev.filter(c => c.group_id !== id));
    setAllocations(prev => {
      const next = new Map(prev);
      removedCatIds.forEach(cid => next.delete(cid));
      return next;
    });
    setToast({ message: 'Deleted!', type: 'info' });
    onSuccess?.();
  }, [setCategoryGroups, setCategories, setAllocations, setToast]);

  const deleteCategory = useCallback(async (id, onSuccess) => {
    if (!window.confirm('Delete this category and all its transactions?')) return;
    const { error } = await supabase.from('categories').delete().eq('id', id);
    if (error) { setToast({ message: 'Delete failed', type: 'error' }); return; }
    setCategories(prev => prev.filter(c => c.id !== id));
    setAllocations(prev => { const next = new Map(prev); next.delete(id); return next; });
    setToast({ message: 'Deleted!', type: 'info' });
    onSuccess?.();
  }, [setCategories, setAllocations, setToast]);

  const renameCategory = useCallback(async (id, name) => {
    const { error } = await supabase.from('categories').update({ name }).eq('id', id);
    if (error) { setToast({ message: 'Rename failed', type: 'error' }); return; }
    setCategories(prev => prev.map(c => c.id === id ? { ...c, name } : c));
    setToast({ message: 'Renamed!', type: 'success' });
  }, [setCategories, setToast]);

  const renameGroup = useCallback(async (id, name, onSuccess) => {
    const { error } = await supabase.from('category_groups').update({ name }).eq('id', id);
    if (error) { setToast({ message: 'Rename failed', type: 'error' }); return; }
    setCategoryGroups(prev => prev.map(g => g.id === id ? { ...g, name } : g));
    setToast({ message: 'Renamed!', type: 'success' });
    onSuccess?.();
  }, [setCategoryGroups, setToast]);

  const saveMonthlyIncome = useCallback(async (value, onSuccess) => {
    const income = parseFloat(value) || 0;
    if (income < 0) { setToast({ message: 'Must be positive', type: 'error' }); return; }
    const { error } = await supabase.from('monthly_income').upsert({
      user_id: user.id,
      month: currentMonth,
      amount: income,
    }, { onConflict: 'user_id,month' });
    if (error) { setToast({ message: 'Save failed', type: 'error' }); return; }
    setMonthlyIncome(income);
    onSuccess?.();
  }, [user, currentMonth, setMonthlyIncome, setToast]);

  const copyPreviousMonth = useCallback(async (onSuccess) => {
    const prevMonth = getPreviousMonthValue(currentMonth);
    const { data: prevIncome } = await supabase.from('monthly_income')
      .select('amount').eq('user_id', user.id).eq('month', prevMonth).single();
    if (prevIncome?.amount) {
      await supabase.from('monthly_income').upsert(
        { user_id: user.id, month: currentMonth, amount: prevIncome.amount },
        { onConflict: 'user_id,month' }
      );
      setMonthlyIncome(prevIncome.amount);
    }
    const { data: prevAllocs, error } = await supabase.from('allocations')
      .select('category_id, amount').eq('user_id', user.id).eq('month', prevMonth);
    if (error) { setToast({ message: 'Copy failed', type: 'error' }); return; }
    if (prevAllocs?.length) {
      const rows = prevAllocs.map(a => ({
        user_id: user.id, category_id: a.category_id, month: currentMonth, amount: a.amount,
      }));
      const { error: upsertErr } = await supabase.from('allocations').upsert(rows, { onConflict: 'user_id,category_id,month' });
      if (upsertErr) { setToast({ message: 'Save failed', type: 'error' }); return; }
      setAllocations(prev => {
        const next = new Map(prev);
        prevAllocs.forEach(a => next.set(a.category_id, a.amount));
        return next;
      });
    }
    setToast({ message: 'Copied!', type: 'success' });
    onSuccess?.();
  }, [user, currentMonth, setMonthlyIncome, setAllocations, setToast]);

  const handleDragEnd = useCallback(async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const cats = categoriesRef.current;
    const draggedCat = cats.find(c => c.id === active.id);
    const overCat = cats.find(c => c.id === over.id);
    if (!draggedCat || !overCat) return;

    if (draggedCat.group_id === overCat.group_id) {
      const groupCats = cats.filter(c => c.group_id === draggedCat.group_id).sort((a, b) => a.sort_order - b.sort_order);
      const newOrder = arrayMove(
        groupCats,
        groupCats.findIndex(c => c.id === draggedCat.id),
        groupCats.findIndex(c => c.id === overCat.id)
      );
      setCategories(prev => prev.map(c => {
        const idx = newOrder.findIndex(nc => nc.id === c.id);
        return idx !== -1 ? { ...c, sort_order: idx } : c;
      }));
      await Promise.all(newOrder.map((cat, i) =>
        supabase.from('categories').update({ sort_order: i }).eq('id', cat.id)
      ));
    } else {
      const targetGroupCats = cats.filter(c => c.group_id === overCat.group_id).sort((a, b) => a.sort_order - b.sort_order);
      const overIndex = targetGroupCats.findIndex(c => c.id === overCat.id);
      const inserted = [
        ...targetGroupCats.slice(0, overIndex),
        { ...draggedCat, group_id: overCat.group_id },
        ...targetGroupCats.slice(overIndex),
      ];
      setCategories(prev => {
        const without = prev.filter(c => c.id !== draggedCat.id);
        return [
          ...without.map(c => {
            const idx = inserted.findIndex(nc => nc.id === c.id);
            return idx !== -1 ? { ...c, sort_order: idx } : c;
          }),
          { ...draggedCat, group_id: overCat.group_id, sort_order: overIndex },
        ];
      });
      await Promise.all(inserted.map((cat, i) =>
        supabase.from('categories')
          .update(cat.id === draggedCat.id ? { group_id: overCat.group_id, sort_order: i } : { sort_order: i })
          .eq('id', cat.id)
      ));
    }
  }, [setCategories]);

  return {
    addCategory,
    addGroup,
    updateBudgetInline,
    deleteGroup,
    deleteCategory,
    renameCategory,
    renameGroup,
    saveMonthlyIncome,
    copyPreviousMonth,
    handleDragEnd,
  };
};
